import { useCallback, useMemo, useState } from 'react';
import {
  isLessonCompleteBlock,
  useTheme,
  type CourseBlock,
  type LessonBlockTranslationFields,
  type LessonBlockTranslationRecord,
  type LessonCompleteBlock,
} from '@course-authoring/shared-types';
import { useEditorStore } from '../store/editorStore';
import { getBlockDisplayTitle, getBlockTypeLabel } from '../utils/blockDisplay';
import {
  filterBlockTranslationUpdates,
  getBlockTranslationFieldDescriptor,
  mergeBlockTranslationFields,
} from './blockTranslationFieldDescriptor';
import { BlockSettingsModal } from './BlockSettingsModal';
import { ContentBlockWrapper } from './ContentBlockWrapper';
import { renderContentCanvasBlockEditor } from './contentCanvasBlockRegistry';
import { LessonCompleteSection } from './LessonCompleteSection';
import { TranslationStatusNote } from './TranslationStatusPrimitives';

interface TranslationCanvasBlockProps {
  record: LessonBlockTranslationRecord;
  fields: LessonBlockTranslationFields;
  isFirst: boolean;
  isLast: boolean;
  readOnly?: boolean;
  onChange: (updates: LessonBlockTranslationFields) => void;
  onPreviewFromHere?: () => void;
}

export function TranslationCanvasBlock({
  record,
  fields,
  isFirst,
  isLast,
  readOnly = false,
  onChange,
  onPreviewFromHere,
}: TranslationCanvasBlockProps) {
  const { tokens } = useTheme();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const setActiveSelection = useEditorStore((state) => state.setActiveSelection);
  const block = useMemo(
    () => mergeBlockTranslationFields(record.sourceBlock, fields),
    [fields, record.sourceBlock],
  );
  const descriptor = getBlockTranslationFieldDescriptor(record.type, record.sourceBlock);
  const canEditInteractionMessage = descriptor?.fields.some(
    ({ key }) => key === 'interactionMessage',
  ) ?? false;
  const handleChange = useCallback((updates: Partial<CourseBlock>) => {
    if (readOnly || !record.editable) return;
    const translatedUpdates = filterBlockTranslationUpdates(record.type, updates, record.sourceBlock);
    if (Object.keys(translatedUpdates).length > 0) onChange(translatedUpdates);
  }, [onChange, readOnly, record.editable, record.type]);

  return (
    <div onClick={() => setActiveSelection(record.blockId)} data-translation-block-id={record.blockId}>
      <ContentBlockWrapper
        blockId={record.blockId}
        blockTypeLabel={getBlockTypeLabel(record.sourceBlock)}
        blockDisplayTitle={getBlockDisplayTitle(record.sourceBlock)}
        isFirst={isFirst}
        isLast={isLast}
        isLocked={record.sourceBlock.locked === true}
        contentInert={readOnly}
        translationStatus={record.status}
        onPreviewFromHere={onPreviewFromHere}
        onOpenSettings={canEditInteractionMessage
          && !readOnly
          && record.editable
          && !record.sourceBlock.locked
          ? () => setSettingsOpen(true)
          : undefined}
      >
        {record.status ? (
          <div style={{ marginBottom: tokens.spacing.md }}>
            <TranslationStatusNote status={record.status} subject="block" />
          </div>
        ) : null}
        {record.sourceBlock.locked ? (
          <div role="note" style={{ marginBottom: tokens.spacing.md, color: tokens.text.muted }}>
            This source block is locked and cannot be translated.
          </div>
        ) : null}
        {descriptor ? renderContentCanvasBlockEditor({
          block,
          autoFocus: false,
          onChange: handleChange,
          onSetBasicContentLayout: () => undefined,
          translationMode: true,
        }) : (
          <div role="note" style={{ color: tokens.text.muted }}>
            This block has no editable translation fields.
          </div>
        )}
      </ContentBlockWrapper>
      {settingsOpen ? (
        <BlockSettingsModal
          block={record.sourceBlock}
          onClose={() => setSettingsOpen(false)}
          onSave={handleChange}
          translationMode
          interactionMessageValue={typeof fields.interactionMessage === 'string'
            ? fields.interactionMessage
            : ''}
          readOnly={readOnly || !record.editable}
        />
      ) : null}
    </div>
  );
}

export function TranslationCanvasLessonComplete({
  record,
  fields,
  readOnly = false,
  onChange,
}: Pick<TranslationCanvasBlockProps, 'record' | 'fields' | 'readOnly' | 'onChange'>) {
  const block = useMemo(
    () => mergeBlockTranslationFields(record.sourceBlock, fields),
    [fields, record.sourceBlock],
  );
  const handleChange = useCallback((updates: Partial<CourseBlock>) => {
    if (readOnly || !record.editable) return;
    const translatedUpdates = filterBlockTranslationUpdates(record.type, updates, record.sourceBlock);
    if (Object.keys(translatedUpdates).length > 0) onChange(translatedUpdates);
  }, [onChange, readOnly, record.editable, record.type]);

  if (!isLessonCompleteBlock(block)) return null;

  return (
    <LessonCompleteSection
      block={block as LessonCompleteBlock}
      onChange={handleChange}
      isLocked={record.sourceBlock.locked === true}
      translationStatus={record.status}
      translationNote={record.status ? <TranslationStatusNote status={record.status} subject="block" /> : null}
      translationMode
    />
  );
}
